import type { ApiClient } from '@carbonwebmail/api-client'
import type { TicketThreadDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { CreateCard } from '../../ui/CreateCard.js'
import { Field } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import { Table, Td, Tr } from '../../ui/Table.js'
import { useSupport } from './useSupport.js'

/**
 * Asking us for help, without leaving the product.
 *
 * **A thread, not a contact form.** The first reply to a support request is
 * usually a question, and a form that only sends the opening message moves the
 * conversation to email — where the customer has one version and we have
 * another, and the one an operator reads is whichever was opened last.
 *
 * Available to every role including a viewer: somebody who can only look at
 * the product is exactly who needs to be able to say that something is wrong
 * with it.
 */

/** What each status means to the person who opened it, in their terms. */
const STATUS_WORDS: Readonly<Record<string, string>> = {
  open: 'Waiting for us',
  answered: 'We replied',
  closed: 'Closed',
}

export function SupportPage({ client }: { readonly client: ApiClient }) {
  const support = useSupport({ client })
  const { state, thread, notice, problem, busy } = support

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  return (
    <>
      <header className="mb-6">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Support</h1>
        <p className="mt-1 text-[13px] text-t2">
          Ask us anything about Carbon Mail. Replies appear here, and we will email you when one
          arrives.
        </p>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

      <CreateCard
        heading="Ask for help"
        submit="Send"
        disabled={busy || subject.trim() === '' || body.trim() === ''}
        /*
         * Cleared only when it actually sent.
         *
         * The action resolves either way — it reports a failure by setting
         *  rather than by rejecting — so clearing unconditionally
         * threw away what somebody had typed at exactly the moment they most
         * needed it back.
         */
        onSubmit={() => {
          void support.open(subject, body).then((sent) => {
            if (!sent) return
            setSubject('')
            setBody('')
          })
        }}
      >
        <Field
          id="ticket-subject"
          label="Subject"
          type="text"
          value={subject}
          disabled={busy}
          hint="A few words about what this is."
          onChange={setSubject}
        />
        <label className="mt-3 block font-medium text-[13px] text-t1" htmlFor="ticket-body">
          What is happening
        </label>
        <textarea
          id="ticket-body"
          value={body}
          disabled={busy}
          rows={5}
          onChange={(event) => setBody(event.target.value)}
          className="mt-1 w-full rounded-field border border-line bg-surface px-3 py-2 text-[13px] text-t1"
        />
        <p className="mt-1 text-[12px] text-t3">
          Tell us what you expected and what happened instead. Include a campaign or contact name if
          it helps — please do not paste passwords or API keys.
        </p>
      </CreateCard>

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Loading…</p>}
        onRetry={() => void support.retry()}
      >
        {(loaded) =>
          loaded.tickets.length === 0 ? (
            <p className="text-[13px] text-t3">You have not asked us anything yet.</p>
          ) : (
            <>
              <h2 className="mb-3 font-bold text-[15px] text-t1">Your tickets</h2>
              <Table head={['Subject', 'Status', 'Messages', 'Last activity', '']} minWidth={720}>
                {loaded.tickets.map((ticket) => (
                  <Tr key={ticket.id}>
                    <Td strong>{ticket.subject}</Td>
                    <Td>{STATUS_WORDS[ticket.status] ?? ticket.status}</Td>
                    <Td>{ticket.messageCount}</Td>
                    <Td>{when(ticket.updatedAt)}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => void support.openThread(ticket.id)}
                        className="font-medium text-[13px] text-brand"
                      >
                        Open
                      </button>
                    </Td>
                  </Tr>
                ))}
              </Table>
            </>
          )
        }
      </Async>

      {thread === undefined ? null : (
        <Thread
          thread={thread}
          busy={busy}
          onReply={(message) => support.reply(thread.ticket.id, message)}
          onClose={() => void support.close(thread.ticket.id)}
          onDismiss={support.closeThread}
        />
      )}
    </>
  )
}

/**
 * One conversation.
 *
 * The two sides are labelled and styled apart, because a reply from us
 * rendered like the customer's own message reads as their own words — and the
 * first thing somebody does with a support thread is scan it for what *we*
 * said.
 */
function Thread({
  thread,
  busy,
  onReply,
  onClose,
  onDismiss,
}: {
  readonly thread: TicketThreadDto
  readonly busy: boolean
  readonly onReply: (body: string) => Promise<boolean>
  readonly onClose: () => void
  readonly onDismiss: () => void
}) {
  const [reply, setReply] = useState('')

  return (
    <section className="mt-8 max-w-2xl rounded-card border border-line bg-surface p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <h2 className="font-bold text-[15px] text-t1">{thread.ticket.subject}</h2>
        <button type="button" onClick={onDismiss} className="text-[13px] text-t3">
          Close
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-3">
        {thread.messages.map((message) => (
          <div
            key={message.id}
            className={
              message.side === 'operator'
                ? 'rounded-card border border-brand/30 bg-brand/5 p-3'
                : 'rounded-card border border-line p-3'
            }
          >
            <p className="mb-1 text-[12px] text-t3">
              {message.side === 'operator' ? 'Carbon Mail' : message.author} · {when(message.at)}
            </p>
            {/* Pre-wrap: somebody's line breaks are part of what they wrote. */}
            <p className="whitespace-pre-wrap text-[13px] text-t1">{message.body}</p>
          </div>
        ))}
      </div>

      <label className="block font-medium text-[13px] text-t1" htmlFor="ticket-reply">
        Reply
      </label>
      <textarea
        id="ticket-reply"
        value={reply}
        disabled={busy}
        rows={4}
        onChange={(event) => setReply(event.target.value)}
        className="mt-1 w-full rounded-field border border-line bg-surface px-3 py-2 text-[13px] text-t1"
      />

      <div className="mt-3 flex gap-3">
        <button
          type="button"
          disabled={busy || reply.trim() === ''}
          // Same rule as the opening form: a failed reply keeps its text.
          onClick={() => {
            void onReply(reply).then((sent) => {
              if (sent) setReply('')
            })
          }}
          className="rounded-field bg-brand px-3 py-1.5 font-medium text-[13px] text-white disabled:opacity-60"
        >
          Send reply
        </button>
        {thread.ticket.status === 'closed' ? (
          <p className="self-center text-[12px] text-t3">This is closed. Replying reopens it.</p>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="font-medium text-[13px] text-t3 disabled:opacity-60"
          >
            Close this ticket
          </button>
        )}
      </div>
    </section>
  )
}
